import {
  MessageEnvelope,
  MessageListBucketMeta,
  MessageListItem,
  MessageListMessage,
} from '../../schema/message';
import { createQuerySeparator, createDateSeparator } from './create-separator';
import { getMessageBucket, QueryGroupRange } from './get-message-bucket';
import { getMessageDayKey } from './message-date-time';

export const buildGroupedMessages = (
  visibleMessages: MessageEnvelope[],
  growGroupRanges: QueryGroupRange[]
): MessageListItem[] => {
  const groupedMessages: MessageListItem[] = [];
  const growRangeIndices = new Map<
    string,
    {
      firstIndex: number;
      lastIndex: number;
    }
  >();

  for (let index = 0; index < visibleMessages.length; index++) {
    const bucket = getMessageBucket(visibleMessages[index], growGroupRanges);
    if (bucket.type !== 'query') {
      continue;
    }

    const existingRange = growRangeIndices.get(bucket.key);
    if (existingRange) {
      existingRange.lastIndex = index;
      continue;
    }

    growRangeIndices.set(bucket.key, {
      firstIndex: index,
      lastIndex: index,
    });
  }

  for (let index = 0; index < visibleMessages.length; index++) {
    const message = visibleMessages[index];
    const bucket = getMessageBucket(message, growGroupRanges);
    const prevMessage = index > 0 ? visibleMessages[index - 1] : undefined;
    const currentDayKey = getMessageDayKey(message);
    const prevDayKey = prevMessage ? getMessageDayKey(prevMessage) : undefined;
    const growRange =
      bucket.type === 'query' ? growRangeIndices.get(bucket.key) : undefined;

    const isFirstGrowMessageInRange =
      bucket.type === 'query' && growRange?.firstIndex === index;
    const isLastGrowMessageInRange =
      bucket.type === 'query' && growRange?.lastIndex === index;
    const hasDayChangedFromPrev = !prevMessage || currentDayKey !== prevDayKey;

    if (hasDayChangedFromPrev) {
      groupedMessages.push(createDateSeparator(currentDayKey));
    }

    if (isFirstGrowMessageInRange) {
      groupedMessages.push(
        createQuerySeparator(
          bucket as Extract<MessageListBucketMeta, { type: 'query' }>,
          'start'
        )
      );
    }

    const messageItem: MessageListMessage = {
      ...message,
      messageListBucket: bucket,
    };
    groupedMessages.push(messageItem);

    if (isLastGrowMessageInRange) {
      groupedMessages.push(
        createQuerySeparator(
          bucket as Extract<MessageListBucketMeta, { type: 'query' }>,
          'end'
        )
      );
    }
  }

  return groupedMessages;
};
